import { matchPath } from 'react-router'

/** Custom URL scheme the desktop app registers; build links via buildDeepLink. */
export const MANGO_SCHEME = 'mango://'

/** `mango://` deep link from an in-app path (no leading slash); bare = default route. */
export const buildDeepLink = (path = ''): string => `${MANGO_SCHEME}${path}`

/** Path segments: letters, digits and the unreserved/ID-friendly `-._~%@+=,`. */
const SAFE_PATH_SEGMENT = /^[\w\-.~%@+=,]+$/
/** Query/hash: anything printable except quotes, angle brackets, backticks and backslashes. */
const SAFE_SUFFIX = /^[^\s<>"'`\\]*$/
const PATH_SUFFIX_SPLIT = /^([^?#]*)(\?[^#]*)?(#.*)?$/

/** Deep-link first segments that differ from the web-app route they open. */
const LEGACY_DEEP_LINK_ALIASES: Record<string, string> = {
  analytics: 'call-analytics',
}

/**
 * First segments of the main-shell (`/`) child routes in `router.tsx`; keep in
 * sync (`desktopDeepLink.test.ts` checks it against the router source). Deep
 * links elsewhere would unmount the shell, and with it the phone and the
 * deep-link listener.
 */
export const MAIN_SHELL_ROUTE_SEGMENTS = [
  'calls',
  'missed-calls',
  'voicemails',
  'messaging',
  'faxes',
  'contacts',
  'users',
  'call-analytics',
  'web-phone',
  'reporting',
  'settings',
  'hotels',
  'admin',
  'status',
  'schedules',
  'integrations',
]

/** Whether `path` (from {@link deepLinkToInAppPath}) is the root or a main-shell route. */
export const isMainShellPath = (path: string): boolean => {
  const firstSegment = /^\/([^/?#]*)/.exec(path)?.[1] ?? ''
  return firstSegment === '' || MAIN_SHELL_ROUTE_SEGMENTS.includes(firstSegment)
}

/**
 * Multi-segment children of the `settings/*` route in `router.tsx`; keep in
 * sync (`desktopDeepLink.test.ts` checks it against the router source). These
 * pass through unchanged instead of taking the legacy `#section` mapping.
 */
export const NESTED_SETTINGS_ROUTES = [
  'users/:userId',
  'user-groups/add',
  'user-groups/:userGroupId/edit',
  'messaging-groups/:groupId',
  'switchboards/:extensionDialPlan',
  'devices/new',
  'devices/:deviceId',
  'sound-clips/new',
  'sound-clips/:soundClipId',
  'music-on-hold/:playlistId',
  'phone-numbers/:phoneNumberId',
  'extensions/:type/:extensionId',
  'hotels/settings',
  'hotels/wakeup',
  'hotels/wakeup-history',
]

const LEGACY_SETTINGS_SECTION_PATH =
  /^\/settings\/([^/?#]+)\/([^/?#]+)(\?[^#]*)?(#.*)?$/

/**
 * Legacy `settings/<pane>/<section>` links open `/settings/<pane>#<section>`
 * unless they name a real nested settings route. The query is kept; an
 * explicit hash wins over the section.
 */
const mapLegacySettingsSection = (path: string): string => {
  const parts = LEGACY_SETTINGS_SECTION_PATH.exec(path)
  if (!parts) return path
  const [, pane, section, search = '', hash = ''] = parts

  const isNestedRoute = NESTED_SETTINGS_ROUTES.some((route) =>
    matchPath(`/settings/${route}`, `/settings/${pane}/${section}`),
  )
  if (isNestedRoute) return path

  return `/settings/${pane}${search}${hash || `#${section}`}`
}

/**
 * Whether `segment` is `.`/`..` once percent-decoded (URL parsers resolve
 * `%2e%2e` like `..`), or can't be decoded at all.
 */
const isDotOrUndecodableSegment = (segment: string): boolean => {
  try {
    const decoded = decodeURIComponent(segment)
    return decoded === '.' || decoded === '..'
  } catch {
    return true
  }
}

/**
 * Normalizes `input` to a same-app route (`/path?query#hash`), or null when it
 * could leave the app or carries unexpected characters. A missing leading
 * slash is added and a trailing slash dropped; dot segments (literal or
 * percent-encoded), undecodable segments and empty segments (which would form
 * `//host`) are rejected.
 */
export const toSafeInAppPath = (input: string): string | null => {
  const parts = PATH_SUFFIX_SPLIT.exec(input)
  if (!parts) return null
  const [, rawPath, search = '', hash = ''] = parts

  const segments = rawPath.replace(/^\//, '').replace(/\/$/, '').split('/')
  const isRoot = segments.length === 1 && segments[0] === ''
  const hasUnsafeSegment = segments.some(
    (segment) =>
      !SAFE_PATH_SEGMENT.test(segment) || isDotOrUndecodableSegment(segment),
  )
  if (!isRoot && hasUnsafeSegment) return null
  if (!SAFE_SUFFIX.test(`${search}${hash}`)) return null

  return `/${isRoot ? '' : segments.join('/')}${search}${hash}`
}

/** `mango://` deep link that opens `path` (an in-app route with leading slash). */
export const buildDeepLinkForPath = (path: string): string =>
  buildDeepLink(path.replace(/^\//, ''))

/**
 * Resolves an incoming `mango://…` URL to the in-app route it should open, or
 * null when it isn't a `mango:` URL or fails {@link toSafeInAppPath}.
 */
export const deepLinkToInAppPath = (url: string): string | null => {
  const schemePrefix = /^mango:(\/\/)?/i.exec(url)
  if (!schemePrefix) return null

  const path = toSafeInAppPath(url.slice(schemePrefix[0].length))
  if (!path) return null

  const resolved = path.replace(
    /^\/([^/?#]+)/,
    (_match, firstSegment: string) => {
      const segment = firstSegment.toLowerCase()
      return `/${LEGACY_DEEP_LINK_ALIASES[segment] ?? segment}`
    },
  )
  return mapLegacySettingsSection(resolved)
}

/**
 * Allowlist of email-reachable deep-link destinations, keyed so emails link to
 * `/open?to=<key>` — stable, and webmail-safe unlike a raw `mango://` href. Not a
 * security boundary: electron re-validates every path and each is auth-gated.
 */
export const EMAIL_DEEP_LINK_TARGETS = {
  // Update button (CUS-877): Updates pane; `?check` auto-checks on mount.
  updates: 'settings/updates?check',
  // Notifications opt-in: same pane, no auto-check.
  'release-notifications': 'settings/updates',
} as const

export type EmailDeepLinkTarget = keyof typeof EMAIL_DEEP_LINK_TARGETS

/** Fallback for missing/unknown `?to=`; `updates` matches legacy /desktop-update. */
export const DEFAULT_EMAIL_DEEP_LINK_TARGET: EmailDeepLinkTarget = 'updates'

/** Resolve an email `?to=<key>` to a `mango://` deep link (default for unknown/missing). */
export const resolveEmailDeepLink = (key?: string | null): string => {
  // hasOwnProperty, not `in`: `in` walks the prototype, so `?to=toString` would "match".
  const target =
    key && Object.prototype.hasOwnProperty.call(EMAIL_DEEP_LINK_TARGETS, key)
      ? (key as EmailDeepLinkTarget)
      : DEFAULT_EMAIL_DEEP_LINK_TARGET
  return buildDeepLink(EMAIL_DEEP_LINK_TARGETS[target])
}

/**
 * Hand a `mango://` deep link to the OS via a transient hidden iframe. Assigning
 * the top document's `location.href` instead risks Firefox replacing the page
 * with an error page when no handler is registered — and the interstitial must
 * stay put so its always-visible download fallback shows (the app-less
 * recipients are exactly the ones who'd hit that). The iframe contains it.
 */
export const openDesktopApp = (deepLink: string): void => {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  document.body.appendChild(iframe)
  const frameWindow = iframe.contentWindow
  try {
    // Always set for a fresh iframe; guard is type-only. No top-level fallback —
    // that would reintroduce the Firefox error-page navigation this avoids.
    if (frameWindow) {
      frameWindow.location.href = deepLink
    }
  } catch {
    // Swallow a synchronous throw on an unregistered scheme (this runs in a
    // mount effect). No retry — the manual button + download link recover it.
  }
  // Removing the iframe too soon cancels the still-pending scheme hand-off; a
  // fixed delay is the hardened pattern. The empty hidden frame is harmless.
  window.setTimeout(() => iframe.remove(), 1000)
}
